import { useId, type ReactNode } from 'react';

import { Reveal } from '@/components/Reveal';
import { cn } from '@/lib/utils';

/*
 * The `/settings` building blocks (screens.md §/settings): a section is a
 * heading (+ an optional one-line description) over plain rows; a row is a
 * label + description on the left and its control on the right (stacked
 * below `sm`). No surfaces, no dividers — space separates the sections
 * (ui-ux.md §3 "Settings rows"). With an `order`, the section takes its
 * step in the screen's load settle (one light rise per section, ui-ux.md §4).
 */

export function SettingsSection({
  title,
  description,
  id,
  className,
  order,
  children,
}: {
  title: string;
  description?: string;
  /** Anchor for deep links (`/settings#notifications`). */
  id?: string;
  /** On the wrapper when `order` is set, so a hidden section leaves no empty box in the column's gap. */
  className?: string;
  /** Step in the enclosing `RevealGroup` (the settings screen's settle). */
  order?: number;
  children: ReactNode;
}) {
  const headingId = useId();
  const section = (
    <section
      id={id}
      aria-labelledby={headingId}
      className={cn('flex scroll-mt-8 flex-col gap-2', order === undefined && className)}
    >
      <div className="flex flex-col gap-1 pb-2">
        <h2 id={headingId} className="text-section text-ink">
          {title}
        </h2>
        {description ? <p className="prose-measure text-caption text-ink-secondary">{description}</p> : null}
      </div>
      {children}
    </section>
  );
  if (order === undefined) return section;
  return (
    <Reveal order={order} className={className}>
      {section}
    </Reveal>
  );
}

export function SettingRow({
  title,
  description,
  control,
  children,
}: {
  title: string;
  description?: string;
  /** The row's control, right-aligned from `sm`. */
  control?: ReactNode;
  /** Anything that belongs under the row (a status line, a dialog). */
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-headline text-ink">{title}</p>
        {description ? <p className="prose-measure text-caption text-ink-secondary">{description}</p> : null}
      </div>
      {control ? <div className="flex shrink-0 items-center gap-2">{control}</div> : null}
      {children}
    </div>
  );
}
